import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import './KGVisualization.css'

interface KGNode {
  id: string
  label: string
  type: string
  properties: Record<string, any>
  isCenter?: boolean
  expanded?: boolean
}

interface KGLink {
  source: string
  target: string
  type: string
  properties: Record<string, any>
}

interface KGSubgraph {
  nodes: KGNode[]
  links: KGLink[]
  total_nodes: number
  total_links: number
}

interface KGVisualizationProps {
  data: KGSubgraph | null
  onNodeClick?: (node: KGNode) => void
}

const KGVisualization: React.FC<KGVisualizationProps> = ({ data, onNodeClick }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const simulationRef = useRef<d3.Simulation<KGNode, KGLink> | null>(null)
  const nodesRef = useRef<d3.Selection<SVGGElement, KGNode, SVGGElement, any> | null>(null)
  const linksRef = useRef<d3.Selection<SVGLineElement, KGLink, SVGGElement, any> | null>(null)
  const linkTextsRef = useRef<d3.Selection<SVGTextElement, KGLink, SVGGElement, any> | null>(null)

  useEffect(() => {
    if (!data || !containerRef.current || !svgRef.current) return

    const container = containerRef.current
    const width = container.clientWidth
    const height = container.clientHeight

    // 清空SVG
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()

    // 创建工具提示
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', 'kg-tooltip')
      .style('position', 'absolute')
      .style('padding', '12px')
      .style('background', 'rgba(255, 255, 255, 0.95)')
      .style('border', '1px solid #ddd')
      .style('border-radius', '8px')
      .style('pointer-events', 'none')
      .style('font-family', 'Microsoft YaHei, sans-serif')
      .style('font-size', '13px')
      .style('box-shadow', '0 2px 8px rgba(0,0,0,0.15)')
      .style('max-width', '320px')
      .style('z-index', '1000')
      .style('opacity', 0)

    // 颜色映射方案
    const color = d3.scaleOrdinal()
      .domain(['标准文献', '组织机构', '起草人员', '标准类别', 'CCS分类', 'ICS分类', '术语', 'center'])
      .range(['#2E86C1', '#E74C3C', '#27AE60', '#8E44AD', '#F39C12', '#16A085', '#C0392B', '#FF0000'])

    // 定义加深颜色的函数
    const darkenColor = (hex: string, factor = 0.7) => {
      let r = parseInt(hex.slice(1, 3), 16)
      let g = parseInt(hex.slice(3, 5), 16)
      let b = parseInt(hex.slice(5, 7), 16)

      r = Math.round(r * factor)
      g = Math.round(g * factor)
      b = Math.round(b * factor)

      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
    }

    // 创建SVG容器
    svg.attr('width', width).attr('height', height)

    // 添加缩放功能
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', ({ transform }) => {
        containerGroup.attr('transform', transform)
      })

    svg.call(zoom)

    const containerGroup = svg.append('g')

    // 定义箭头
    svg.append('defs').append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 35)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')

    // 调试信息
    console.log('Graph data:', data)
    const centerNode = data.nodes.find(n => n.isCenter)
    if (!centerNode) {
      console.warn('No center node found in the data!')
    }

    // 创建节点ID集合，用于快速查找
    const nodeIds = new Set(data.nodes.map(n => n.id))
    
    // 过滤掉无效的边
    const validLinks = data.links.filter(link => 
      nodeIds.has(link.source.id || link.source) && 
      nodeIds.has(link.target.id || link.target)
    )

    // 创建力导向模拟
    const simulation = d3.forceSimulation(data.nodes)
      .force('link', d3.forceLink(validLinks).id((d: any) => d.id).distance(150))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('collide', d3.forceCollide(80))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY(height / 2).strength(0.05))

    // 如果存在中心节点，将其固定在中心位置
    if (centerNode) {
      simulation.force('x', d3.forceX(width / 2).strength(0.1))
        .force('y', d3.forceY(height / 2).strength(0.1))
      centerNode.fx = width / 2
      centerNode.fy = height / 2
    }

    // 设置模拟参数
    simulation.alphaDecay(0.02).velocityDecay(0.3)

    // 创建连接线
    const link = containerGroup.append('g')
      .selectAll('.link')
      .data(validLinks)
      .join('line')
      .attr('class', 'link')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1.5)
      .attr('marker-end', 'url(#arrow)')

    // 创建连接线文字
    const linkText = containerGroup.append('g')
      .selectAll('.link-text')
      .data(validLinks)
      .join('text')
      .attr('class', 'link-text')
      .attr('font', '12px Microsoft YaHei, sans-serif')
      .attr('fill', '#666')
      .attr('pointer-events', 'none')
      .text((d: any) => d.type)

    // 创建节点组
    const node = containerGroup.append('g')
      .selectAll('.node')
      .data(data.nodes)
      .join('g')
      .attr('class', (d: any) => `node ${d.isCenter ? 'center' : ''}`)
      .style('cursor', 'pointer')
      .call(d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended))

    // 绘制节点圆形
    node.append('circle')
      .attr('r', (d: any) => {
        if (d.isCenter) return 35
        if (d.type === '标准文献') return 25
        if (d.type === '组织机构') return 20
        return 15
      })
      .attr('fill', (d: any) => d.isCenter ? color('center') : color(d.type))
      .attr('stroke', (d: any) => d.isCenter ? '#000' : darkenColor(color(d.type)))
      .attr('stroke-width', (d: any) => d.isCenter ? 2 : 1.5)

    // 添加节点文字
    node.append('text')
      .attr('class', (d: any) => `node-text ${d.isCenter ? 'center' : ''}`)
      .text((d: any) => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', '.3em')
      .attr('fill', (d: any) => d.isCenter ? '#fff' : '#333')
      .attr('font-weight', (d: any) => d.isCenter ? 'bold' : 'normal')
      .attr('font-size', (d: any) => d.isCenter ? '14px' : '12px')
      .attr('text-shadow', (d: any) => d.isCenter ? '0 1px 2px rgba(0,0,0,0.5)' : 'none')
      .attr('paint-order', (d: any) => d.isCenter ? 'stroke' : 'normal')
      .attr('stroke', (d: any) => d.isCenter ? '#000' : 'none')
      .attr('stroke-width', (d: any) => d.isCenter ? '0.5px' : '0px')
      .attr('font-family', 'Microsoft YaHei, sans-serif')
      .attr('pointer-events', 'none')

    // 节点悬停效果
    node.on('mouseover', function(event, d: any) {
      // 高亮当前节点
      d3.select(this).select('circle')
        .attr('fill', (d: any) => d.isCenter ? darkenColor(color('center')) : darkenColor(color(d.type)))

      // 显示工具提示
      tooltip.transition()
        .duration(200)
        .style('opacity', 0.95)

      // 构建工具提示内容
      let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px; color: #2c3e50; border-bottom: 1px solid #eee; padding-bottom: 8px; font-size: 14px;">${d.label}</div>`
      tooltipContent += `<div style="color: #34495e;">`
      
      // 添加节点类型
      tooltipContent += `<div style="margin: 6px 0; display: flex; align-items: flex-start;">
        <span style="color: #7f8c8d; margin-right: 8px; min-width: 80px;">类型:</span>
        <span style="flex: 1; word-break: break-all;">${d.type}</span>
      </div>`
      
      // 添加节点属性
      if (d.properties) {
        Object.entries(d.properties).forEach(([key, value]) => {
          if (value && key !== 'id' && key !== 'type' && key !== 'label') {
            tooltipContent += `<div style="margin: 6px 0; display: flex; align-items: flex-start;">
              <span style="color: #7f8c8d; margin-right: 8px; min-width: 80px;">${key}:</span>
              <span style="flex: 1; word-break: break-all;">${value}</span>
            </div>`
          }
        })
      }

      tooltipContent += `</div>`

      tooltip.html(tooltipContent)
        .style('left', (event.pageX + 15) + 'px')
        .style('top', (event.pageY - 28) + 'px')
    })
    .on('mouseout', function(event, d: any) {
      // 恢复节点颜色
      d3.select(this).select('circle')
        .attr('fill', (d: any) => d.isCenter ? color('center') : color(d.type))

      // 隐藏工具提示
      tooltip.transition()
        .duration(500)
        .style('opacity', 0)
    })
    // .on('click', function(event, d: any) {
    //   // 点击节点事件
    //   event.stopPropagation()
    //   if (onNodeClick) {
    //     onNodeClick(d)
    //   }
    // })

    // 更新函数
    simulation.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y)

      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`)

      linkText
        .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
        .attr('y', (d: any) => (d.source.y + d.target.y) / 2)
    })

    // 拖拽函数
    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.1).restart()
      d.fx = d.x
      d.fy = d.y
    }

    function dragged(event: any, d: any) {
      d.fx = event.x
      d.fy = event.y
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0)
      if (!d.isCenter) {
        d.fx = null
        d.fy = null
      }
    }

    // 添加图例
    const padding = 20
    const legend = svg.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${width - 150 - padding}, ${padding})`)

    // 添加图例背景矩形
    legend.selectAll('rect')
      .data([0])
      .join('rect')
      .attr('class', 'legend-rect')
      .attr('x', -15)
      .attr('y', -15)
      .attr('width', 130)
      .attr('height', color.domain().length * 25 + 20)
      .attr('stroke', '#ccc')
      .attr('stroke-width', 1)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', 'rgba(255, 255, 255, 0.95)')
      .attr('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))')

    const legendItems = legend.selectAll('.legend-item')
      .data(color.domain())
      .join('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 25})`)

    legendItems.append('circle')
      .attr('class', 'legend-symbol')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', 8)
      .attr('fill', (d: any) => color(d))

    legendItems.append('text')
      .attr('class', 'legend-text')
      .attr('x', 20)
      .attr('y', 5)
      .attr('font-family', 'Microsoft YaHei, sans-serif')
      .attr('fill', '#333')
      .attr('font-size', '12px')
      .text((d: any) => d)

    // 确保图例在最上层
    legend.raise()

    // 保存引用
    simulationRef.current = simulation
    nodesRef.current = node
    linksRef.current = link
    linkTextsRef.current = linkText

    // 清理函数
    return () => {
      d3.select('body').selectAll('.kg-tooltip').remove()
      if (simulationRef.current) {
        simulationRef.current.stop()
      }
    }
  }, [data, onNodeClick])

  if (!data) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: '#999'
      }}>
        暂无图谱数据
      </div>
    )
  }

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: '100%', 
        height: '100%',
        position: 'relative'
      }}
    >
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
    </div>
  )
}

export default KGVisualization
